import { useState } from "react";
import { AlertTriangle, X, ChevronLeft, ChevronRight, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid,
} from "recharts";

export const GREEN = "#2E6B57";
export const CHARCOAL = "#222222";

export function Pill({ tone = "neutral", children, ...rest }) {
  const tones = {
    green: "bg-[#2E6B57]/15 text-[#245544]", amber: "bg-amber-100 text-amber-800",
    red: "bg-red-100 text-red-700", blue: "bg-blue-100 text-blue-800",
    dark: "bg-[#222] text-white", neutral: "bg-black/5 text-[#222]/70",
  };
  return (
    <span className={`inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${tones[tone] || tones.neutral}`} {...rest}>
      {children}
    </span>
  );
}

export function StatCard({ label, value, sub, testid, tone }) {
  return (
    <div className="bg-white rounded-2xl p-4 md:p-5 shadow-sm border border-black/5" data-testid={testid}>
      <p className="text-[10px] md:text-[11px] uppercase tracking-wider text-[#222]/45 font-semibold">{label}</p>
      <p className={`text-2xl md:text-3xl font-bold mt-1 ${tone === "alert" ? "text-red-600" : ""}`} style={{ fontFamily: "Outfit" }}>{value}</p>
      {sub && <p className="text-xs text-[#222]/45 mt-1">{sub}</p>}
    </div>
  );
}

export function Card({ title, right, children, className = "", testid }) {
  return (
    <div className={`bg-white rounded-2xl p-5 shadow-sm border border-black/5 ${className}`} data-testid={testid}>
      {(title || right) && (
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          {title && <h3 className="text-sm font-bold" style={{ fontFamily: "Outfit" }}>{title}</h3>}
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

export function EmptyState({ title, body, testid }) {
  return (
    <div className="bg-white rounded-2xl p-8 md:p-10 text-center border border-dashed border-black/15" data-testid={testid}>
      <h3 className="text-base font-bold mb-1" style={{ fontFamily: "Outfit" }}>{title}</h3>
      <p className="text-sm text-[#222]/55 max-w-md mx-auto">{body}</p>
    </div>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <p className="text-sm text-[#222]/45 flex items-center gap-2 py-4">
      <Loader2 size={14} className="animate-spin" /> {label}
    </p>
  );
}

export function ErrorState({ error, onRetry, testid }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-red-200" data-testid={testid || "admin-error-state"}>
      <p className="text-sm font-semibold text-red-700 mb-1">Couldn't load this data</p>
      <p className="text-xs text-[#222]/60 mb-3">{error || "The request failed. Check your connection and try again."}</p>
      {onRetry && (
        <button onClick={onRetry} className="text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold" data-testid="admin-error-retry">
          Try again
        </button>
      )}
    </div>
  );
}

/** Sub-navigation inside a section. Horizontally scrollable on small screens. */
export function SubTabs({ tabs, active, onChange, testidPrefix = "subtab" }) {
  return (
    <div className="flex gap-1 mb-5 overflow-x-auto -mx-1 px-1 pb-1">
      {tabs.map(t => (
        <button key={t.key} onClick={() => onChange(t.key)}
          className={`px-3.5 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${active === t.key ? "bg-[#222] text-white" : "bg-white border border-black/10 text-[#222]/70 hover:bg-black/5"}`}
          data-testid={`${testidPrefix}-${t.key}`}>
          {t.label}{typeof t.badge === "number" && t.badge > 0 ? ` (${t.badge})` : ""}
        </button>
      ))}
    </div>
  );
}

export function Toggle({ on, onClick, disabled, testid }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className={`w-12 h-6 rounded-full relative shrink-0 transition-colors disabled:opacity-40 ${on ? "bg-[#2E6B57]" : "bg-black/15"}`}
      data-testid={testid} aria-pressed={on}>
      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${on ? "left-6" : "left-0.5"}`} />
    </button>
  );
}

export function RangePicker({ value, onChange, options = [7, 30, 90], testid = "admin-range" }) {
  const [custom, setCustom] = useState("");
  return (
    <div className="flex items-center gap-1" data-testid={testid}>
      {options.map(d => (
        <button key={d} onClick={() => onChange(d)}
          className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${value === d ? "bg-[#222] text-white" : "bg-black/5 text-[#222]/60"}`}
          data-testid={`${testid}-${d}d`}>{d}D</button>
      ))}
      <input type="number" min={7} max={365} value={custom} placeholder="Custom"
        onChange={e => setCustom(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter" && Number(custom) >= 7) onChange(Number(custom)); }}
        className="w-20 px-2 py-1 rounded-full text-[11px] border border-black/10 outline-none focus:ring-1 focus:ring-[#2E6B57]"
        data-testid={`${testid}-custom`} />
    </div>
  );
}

export function Pagination({ page, pageSize, total, onPage, testid = "admin-pagination" }) {
  const pages = Math.max(1, Math.ceil((total || 0) / pageSize));
  if (total === 0) return null;
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-black/5 text-xs" data-testid={testid}>
      <span className="text-[#222]/50">
        {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
      </span>
      <div className="flex items-center gap-1">
        <button disabled={page <= 1} onClick={() => onPage(page - 1)}
          className="p-1.5 rounded-lg bg-black/5 disabled:opacity-30" data-testid={`${testid}-prev`}>
          <ChevronLeft size={14} />
        </button>
        <span className="px-2 font-semibold">{page} / {pages}</span>
        <button disabled={page >= pages} onClick={() => onPage(page + 1)}
          className="p-1.5 rounded-lg bg-black/5 disabled:opacity-30" data-testid={`${testid}-next`}>
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

/** Wide tables scroll horizontally with a sticky header rather than shrinking. */
export function TableShell({ children, testid, maxHeight = "70vh" }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden">
      <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight }}>
        <table className="w-full text-sm min-w-[640px]" data-testid={testid}>{children}</table>
      </div>
    </div>
  );
}

export function Th({ children, className = "" }) {
  return (
    <th className={`text-left px-4 py-3 bg-[#F7F8F7] sticky top-0 z-10 text-[11px] uppercase tracking-wider text-[#222]/50 font-semibold ${className}`}>
      {children}
    </th>
  );
}

export function Drawer({ title, subtitle, onClose, children, testid = "admin-drawer" }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid={testid}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-[#F7F8F7] w-full max-w-xl h-full overflow-y-auto shadow-2xl animate-in slide-in-from-right duration-200">
        <div className="sticky top-0 bg-white border-b border-black/5 px-5 py-4 flex items-start justify-between gap-3 z-10">
          <div className="min-w-0">
            <h2 className="text-lg font-bold truncate" style={{ fontFamily: "Outfit" }}>{title}</h2>
            {subtitle && <p className="text-xs text-[#222]/50 truncate">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg bg-black/5 shrink-0" data-testid={`${testid}-close`}>
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-5">{children}</div>
      </div>
    </div>
  );
}

/** Destructive/consequential confirmation. Always states the exact impact. */
export function ConfirmModal({ title, impact, confirmLabel = "Confirm", danger = false,
  requireReason = true, onConfirm, onClose }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (requireReason && !reason.trim()) return;
    setBusy(true);
    try { await onConfirm(reason.trim()); onClose(); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 grid place-items-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="confirm-modal">
        <div className="flex items-start gap-3 mb-3">
          {danger && <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={20} />}
          <h3 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>{title}</h3>
        </div>
        <div className="bg-[#F7F8F7] rounded-xl p-3 text-sm text-[#222]/75 mb-4 whitespace-pre-line" data-testid="confirm-modal-impact">
          {impact}
        </div>
        {requireReason && (
          <textarea autoFocus rows={3} value={reason} onChange={e => setReason(e.target.value)}
            placeholder="Reason (required) — written to the audit log"
            className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]"
            data-testid="confirm-modal-input" />
        )}
        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm" data-testid="confirm-modal-cancel">Cancel</button>
          <button onClick={submit} disabled={busy || (requireReason && !reason.trim())}
            className={`px-5 py-2 text-sm rounded-full font-semibold text-white disabled:opacity-50 ${danger ? "bg-amber-600" : "bg-[#2E6B57]"}`}
            data-testid="confirm-modal-confirm">
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Legacy reason-confirmation modal, still used by VerificationTab. */
export function ReasonModal({ title, body, danger = false, requireReason = true, onConfirm, onClose }) {
  return (
    <ConfirmModal title={title} impact={body} danger={danger} requireReason={requireReason}
      onConfirm={onConfirm} onClose={onClose} />
  );
}

/** Kept off Overview / Growth / System by design — only rendered where an
 *  export has a real operational reason. */
export function ExportButton({ kind, label, testid }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/exports/${kind}`, {}, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `limya-${kind}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${label} exported — the download is audit-logged.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Export failed"); }
    setBusy(false);
  };
  return (
    <button onClick={run} disabled={busy}
      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold hover:bg-black/10 disabled:opacity-50"
      data-testid={testid || `admin-export-${kind}`}>
      <Download size={13} /> {busy ? "Preparing…" : "Export CSV"}
    </button>
  );
}

/** Trend chart with real empty state — never renders placeholder data. */
export function TrendChart({ data, title, subtitle, dataKey = "count", type = "line",
  height = 220, color = GREEN, bucket = "day", testid }) {
  const empty = !data || data.length === 0 || data.every(d => !d[dataKey]);
  const fmt = d => (bucket === "week" ? `wk ${String(d).slice(5)}` : String(d).slice(5));
  return (
    <Card title={title} testid={testid}>
      {subtitle && <p className="text-xs text-[#222]/45 -mt-2 mb-3">{subtitle}</p>}
      {empty ? (
        <p className="text-sm text-[#222]/45 py-12 text-center">
          No activity in this period yet — nothing is being estimated or filled in.
        </p>
      ) : (
        <div style={{ minWidth: data.length > 45 ? data.length * 14 : undefined }}>
          <ResponsiveContainer width="100%" height={height}>
            {type === "bar" ? (
              <BarChart data={data} margin={{ left: -18, right: 6, top: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#0000000d" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={fmt} interval="preserveStartEnd" minTickGap={18} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip labelFormatter={d => `${bucket === "week" ? "Week of " : ""}${d}`} />
                <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} />
              </BarChart>
            ) : (
              <LineChart data={data} margin={{ left: -18, right: 6, top: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#0000000d" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={fmt} interval="preserveStartEnd" minTickGap={18} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip labelFormatter={d => `${bucket === "week" ? "Week of " : ""}${d}`} />
                <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} />
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

export function KV({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-black/5 py-2 last:border-0">
      <span className="text-xs text-[#222]/55 shrink-0">{label}</span>
      <span className={`text-xs text-right ${mono ? "font-mono" : "font-medium"} break-all`}>{value ?? "—"}</span>
    </div>
  );
}
